'use client';

import { useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { ProfitLossSkeleton } from '@/components';
import { useT } from '@/hooks';
import { useUserPnl } from '@/services/query';
import { PnlDataPoint } from '@/types';
import { ProfitLossChart } from './ProfitLossChart';
import { ProfitLossHeader } from './ProfitLossHeader';
import { ProfitLossStats } from './ProfitLossStats';

interface ProfitLossCardProps {
  address: string;
  totalPnL: number;
  pastWeekPnL: number;
  isLoading?: boolean;
}

export const ProfitLossCard = ({
  address,
  totalPnL,
  isLoading: isProfileLoading,
}: ProfitLossCardProps) => {
  const [timeRange, setTimeRange] = useState('1w');
  const { t } = useT();

  const getRangeParams = (range: string) => {
    switch (range) {
      case '1d':
        return { interval: '1d', fidelity: '1h' };
      case '1w':
        return { interval: '1w', fidelity: '3h' };
      case '1mo':
        return { interval: '1mo', fidelity: '18h' };
      case 'ALL':
        return { interval: 'all', fidelity: '1d' };
      default:
        return { interval: '1d', fidelity: '1h' };
    }
  };

  const rangeParams = getRangeParams(timeRange);

  const {
    data: pnlResponse,
    isLoading: isPnlLoading,
    isFetching: isPnlFetching,
  } = useUserPnl(address, rangeParams.interval, rangeParams.fidelity, {
    enabled: !!address,
  });

  const timeline = pnlResponse?.data || [];
  const chartData = timeline.map((item: PnlDataPoint) => {
    const date = new Date(item.t * 1000);
    return {
      name: date.toISOString(),
      displayDate: date.toLocaleDateString(undefined, {
        weekday: timeRange === '1d' ? undefined : 'short',
        month: timeRange === 'ALL' ? 'short' : undefined,
        day: 'numeric',
        hour: timeRange === '1d' ? 'numeric' : undefined,
      }),
      value: item.p,
    };
  });

  const hasData = !!pnlResponse?.data && pnlResponse.data.length > 0;
  const isLoading = isProfileLoading || (isPnlLoading && !hasData);
  const isRefetching = isPnlFetching && hasData;

  const calculatedTotalPnL = hasData
    ? timeline.reduce((sum: number, item: PnlDataPoint) => sum + item.p, 0)
    : totalPnL;

  // Helper to format the range label for display
  const getSubtitle = () => {
    switch (timeRange) {
      case '1d':
        return t('leaderboardPortfolio.pastDay', { defaultValue: 'Past Day' });
      case '1w':
        return t('leaderboardPortfolio.pastWeek');
      case '1mo':
        return t('leaderboardPortfolio.pastMonth', { defaultValue: 'Past Month' });
      case 'ALL':
        return t('leaderboardPortfolio.allTime', { defaultValue: 'All Time' });
      default:
        return t('leaderboardPortfolio.totalPnl', { defaultValue: 'Total PnL' });
    }
  };

  const subtitle = getSubtitle();

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        p: 1.5,
        overflow: 'hidden',
      }}
    >
      {isLoading ? (
        <ProfitLossSkeleton />
      ) : (
        <>
          <ProfitLossHeader timeRange={timeRange} onRangeChange={setTimeRange} />

          {isRefetching ? (
            <ProfitLossSkeleton hideHeader />
          ) : (
            <>
              <ProfitLossStats totalPnL={calculatedTotalPnL} subtitle={subtitle} />
              <ProfitLossChart data={chartData} />
            </>
          )}
        </>
      )}
    </Box>
  );
};
